import axios from "axios";

/*
  PRODUCTION ONLY
  Fix token upload material:
  - Menormalisasi token dari response login.
  - Semua request /api diberi Authorization Bearer.
  - Mencakup axios dan fetch.
  - Jika token invalid, diarahkan login ulang.
*/

(function () {
  if (typeof window === "undefined") return;

  const p = window.location.pathname || "";
  const isStaging =
    p === "/staging" ||
    p.startsWith("/staging/") ||
    p === "/staging-public" ||
    p.startsWith("/staging-public/");

  if (isStaging) return;
  if (window.__PRODUCTION_TOKEN_UPLOAD_FINAL_FIX__) return;

  window.__PRODUCTION_TOKEN_UPLOAD_FINAL_FIX__ = true;

  function safeGet(key) {
    try {
      return window.localStorage.getItem(key) || window.sessionStorage.getItem(key) || "";
    } catch (_e) {
      return "";
    }
  }

  function safeSet(key, value) {
    try {
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        value !== "undefined" &&
        value !== "null"
      ) {
        window.localStorage.setItem(key, value);
      }
    } catch (_e) {}
  }

  function safeRemove(key) {
    try {
      window.localStorage.removeItem(key);
      window.sessionStorage.removeItem(key);
    } catch (_e) {}
  }

  function extractToken(obj) {
    if (!obj || typeof obj !== "object") return "";

    return (
      obj.token ||
      obj.accessToken ||
      obj.authToken ||
      obj.jwt ||
      obj.data?.token ||
      obj.data?.accessToken ||
      obj.data?.authToken ||
      obj.data?.jwt ||
      obj.user?.token ||
      obj.user?.accessToken ||
      obj.user?.authToken ||
      obj.user?.jwt ||
      obj.data?.user?.token ||
      obj.data?.user?.accessToken ||
      obj.data?.user?.authToken ||
      obj.data?.user?.jwt ||
      ""
    );
  }

  function normalizeLoginPayload(payload) {
    const token = extractToken(payload);

    let user =
      payload?.user ||
      payload?.data?.user ||
      payload?.data ||
      payload ||
      {};

    if (!user || typeof user !== "object") user = {};

    if (token) {
      user.token = token;
      user.accessToken = user.accessToken || token;
      safeSet("token", token);
      safeSet("accessToken", token);
    }

    try {
      const oldRaw = safeGet("user");
      const oldUser = oldRaw ? JSON.parse(oldRaw) : {};
      const merged = { ...oldUser, ...user };

      if (token) {
        merged.token = token;
        merged.accessToken = merged.accessToken || token;
      }

      window.localStorage.setItem("user", JSON.stringify(merged));
    } catch (_e) {
      try {
        window.localStorage.setItem("user", JSON.stringify(user));
      } catch (__e) {}
    }

    return token;
  }

  function getToken() {
    const direct =
      safeGet("token") ||
      safeGet("accessToken") ||
      safeGet("authToken");

    if (direct && direct !== "undefined" && direct !== "null") return direct;

    const rawUser = safeGet("user");
    if (!rawUser) return "";

    try {
      const user = JSON.parse(rawUser);
      const token = extractToken(user);
      if (token && token !== "undefined" && token !== "null") return token;
    } catch (_e) {}

    return "";
  }

  function clearAuth() {
    ["user", "token", "accessToken", "authToken"].forEach(safeRemove);
  }

  function rewriteUrl(url) {
    let u = String(url || "");
    if (!u) return u;

    u = u.replace("https://office.pt-sip.co.id/api", "/api");
    u = u.replace("http://office.pt-sip.co.id/api", "/api");

    return u;
  }

  function isApiUrl(url) {
    const u = String(url || "");
    return u.startsWith("/api") || u.includes("/api/");
  }

  function isLoginUrl(url) {
    const u = String(url || "").toLowerCase();
    return (
      u.includes("/auth/login") ||
      u.endsWith("/login") ||
      u.includes("/api/login")
    );
  }

  function isInvalidTokenResponse(status, data, url) {
    const msg = String(
      data?.message ||
      data?.error ||
      ""
    ).toLowerCase();

    return (
      status === 401 &&
      isApiUrl(url) &&
      (
        msg.includes("invalid token") ||
        msg.includes("unauthorized") ||
        msg.includes("jwt") ||
        msg.includes("token")
      )
    );
  }

  function redirectLogin() {
    clearAuth();
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/login?expired=1&next=${next}`;
  }

  function attachAxios(instance) {
    if (!instance || instance.__PRODUCTION_TOKEN_UPLOAD_ATTACHED__) return instance;

    instance.__PRODUCTION_TOKEN_UPLOAD_ATTACHED__ = true;

    instance.interceptors.request.use((config) => {
      if (!config) return config;

      if (config.url) config.url = rewriteUrl(config.url);
      if (config.baseURL) config.baseURL = rewriteUrl(config.baseURL);

      const fullUrl = `${config.baseURL || ""}${config.url || ""}`;

      if (isApiUrl(fullUrl)) {
        const token = getToken();
        config.headers = config.headers || {};

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          delete config.headers.Authorization;
          delete config.headers.authorization;
        }
      }

      return config;
    });

    instance.interceptors.response.use(
      (res) => {
        const fullUrl = `${res?.config?.baseURL || ""}${res?.config?.url || ""}`;

        if (isLoginUrl(fullUrl) && res?.data) {
          normalizeLoginPayload(res.data);
        }

        return res;
      },
      (err) => {
        const fullUrl = `${err?.config?.baseURL || ""}${err?.config?.url || ""}`;
        const status = err?.response?.status;
        const data = err?.response?.data || {};

        if (isInvalidTokenResponse(status, data, fullUrl)) {
          redirectLogin();
        }

        return Promise.reject(err);
      }
    );

    return instance;
  }

  attachAxios(axios);

  const oldCreate = axios.create.bind(axios);
  axios.create = function patchedCreate(config) {
    return attachAxios(oldCreate(config));
  };

  if (window.fetch && !window.fetch.__PRODUCTION_TOKEN_UPLOAD_FETCH__) {
    const oldFetch = window.fetch.bind(window);

    const patchedFetch = async function (input, init) {
      let url = typeof input === "string" ? input : input?.url || "";
      url = rewriteUrl(url);

      let finalInput = input;

      if (typeof input === "string") {
        finalInput = url;
      } else if (input instanceof Request) {
        finalInput = new Request(url, input);
      }

      const finalInit = init ? { ...init } : {};
      const headers = new Headers(finalInit.headers || (finalInput instanceof Request ? finalInput.headers : undefined));

      if (isApiUrl(url)) {
        const token = getToken();

        if (token) {
          headers.set("Authorization", `Bearer ${token}`);
        } else {
          headers.delete("Authorization");
        }

        finalInit.headers = headers;
      }

      const res = await oldFetch(finalInput, finalInit);

      if (isLoginUrl(url)) {
        try {
          const clone = res.clone();
          const data = await clone.json();
          normalizeLoginPayload(data);
        } catch (_e) {}
      }

      if (res.status === 401 && isApiUrl(url)) {
        try {
          const clone = res.clone();
          const data = await clone.json();
          if (isInvalidTokenResponse(res.status, data, url)) {
            redirectLogin();
          }
        } catch (_e) {}
      }

      return res;
    };

    patchedFetch.__PRODUCTION_TOKEN_UPLOAD_FETCH__ = true;
    window.fetch = patchedFetch;
  }

  window.__CLEAR_PROD_AUTH_AND_LOGIN__ = function () {
    clearAuth();
    window.location.href = "/login";
  };

  window.__CHECK_PROD_TOKEN__ = function () {
    const token = getToken();

    return {
      hasToken: Boolean(token),
      tokenPreview: token ? token.slice(0, 30) + "..." : "",
      user: safeGet("user") ? "ada" : "kosong",
      path: window.location.pathname,
    };
  };
})();
